import { useCallback, useEffect, useRef, useState } from "react";

export interface AnimationStep {
  id: string;
  duration: number;
  action?: () => void;
}

export const useAnimationQueue = (steps: AnimationStep[]) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const currentStep = steps[currentStepIndex] || null;

  const start = useCallback(() => {
    setCurrentStepIndex(0);
    setIsPlaying(true);
  }, []);

  const pause = useCallback(() => {
    setIsPlaying(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    pause();
    setCurrentStepIndex(-1);
  }, [pause]);

  const goToStep = useCallback(
    (index: number) => {
      if (index >= 0 && index < steps.length) {
        setCurrentStepIndex(index);
      }
    },
    [steps.length]
  );

  useEffect(() => {
    if (
      !isPlaying ||
      currentStepIndex < 0 ||
      currentStepIndex >= steps.length
    ) {
      return;
    }

    const step = steps[currentStepIndex];

    if (step.action) {
      step.action();
    }

    if (currentStepIndex < steps.length - 1) {
      timeoutRef.current = setTimeout(() => {
        setCurrentStepIndex((prev) => prev + 1);
      }, step.duration);
    } else {
      setIsPlaying(false);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [currentStepIndex, isPlaying, steps]);

  return {
    currentStep,
    currentStepIndex,
    isPlaying,
    isComplete: currentStepIndex >= steps.length - 1 && !isPlaying,
    start,
    pause,
    reset,
    goToStep,
  };
};
